import { EventEmitter, Injectable } from "@angular/core";
import Dexie, { Table } from "dexie";
import { FileDetail, INotUploadedFileData, Media, PhotoStep } from "src/app/interfaces/PWA/Files";
import { IDynamicSection } from "../interfaces/Outputs/DynamicSection";
import { FormElementPhotos, FormSection, FormUserData } from "../interfaces/PWA/Forms";
import { IStepProgress } from "../interfaces/PWA/Progress";
import { IInspectionOrderSteps } from "../interfaces/Outputs";

// Form Section Interface

@Injectable({
    providedIn: 'root'
})
export class AppDB extends Dexie {
    stepUpdate: EventEmitter<void> = new EventEmitter<void>();

    formSectionsTable!: Table<FormSection, number>;
    formElementPhotosTable!: Table<FormElementPhotos, number>;
    Photo!: Table<PhotoStep, number>;
    NotUploadedFiles!: Table<INotUploadedFileData, number>;

    private _opening?: Promise<void>;

    constructor() {
        super('appdb');
        this.version(1).stores({
            formSectionsTable: '++id, formId, sectionName',
            formElementPhotosTable: '++id, elementId',
            Photo: '++id, photoId',
            NotUploadedFiles: '++id, photoId',
        });
        this.on('versionchange', () => {
            this.close();
        });
        this.on('blocked', () => {
            this.close();
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.ensureHealthy().catch((e) => this.handleFatalIdb(e));
            }
        });
    }

    async initDb() {
        try {
            await this.ensureHealthy();
        } catch (error) {
            console.error("[IDB] Error initializing database:", error);
            this.handleFatalIdb(error);
            throw error;
        }
    }

    private assignTables() {
        this.formSectionsTable = this.table('formSectionsTable');
        this.formElementPhotosTable = this.table('formElementPhotosTable');
        this.Photo = this.table('Photo');
        this.NotUploadedFiles = this.table('NotUploadedFiles');
    }

    private async ensureHealthy(): Promise<void> {
        if (this._opening) {
            return this._opening;
        }

        this._opening = (async () => {
            if (!this.isOpen()) {
                console.log('[IDB] Opening database...');
                try {
                    await this.open();
                    console.log('[IDB] Database opened successfully');
                } catch (e) {
                    this.handleFatalIdb(e);
                    throw e;
                }
            }

            try {
                await this.table('formSectionsTable').limit(1).toArray();
            } catch (e) {
                this.handleFatalIdb(e);
                throw e;
            } finally {
                this.assignTables();
            }
        })();

        try {
            await this._opening;
        } finally {
            this._opening = undefined;
        }
    }

    private handleFatalIdb(e: any) {
        const msg = (e?.name || '') + ' ' + (e?.message || '');
        const isFatal =
            /DatabaseClosedError/i.test(msg) ||
            /UnknownError/i.test(msg) ||
            /Connection to Indexed Database server lost/i.test(msg);

        if (isFatal) {
            console.warn('[IDB] Fatal storage error. Reloading app…', e);
            setTimeout(() => location.reload(), 300);
        }
    }

    async clearDB() {
        this.close();
        await this.delete();
        await this.initDb();
    }

    // Para inicializar la base de datos con las secciones del formulario
    async addFormSections(formSections: IDynamicSection[]) {
        await this.ensureHealthy();
        const formSectionCacheData: FormSection[] = formSections.map((formSection) => ({
            formId: formSection.id_seccion,
            sectionName: formSection.nombre_seccion,
            completed: false,
            formElements: {},
        }));

        const existingSections = await this.formSectionsTable
            .where('formId')
            .anyOf(formSectionCacheData.map((section) => section.formId))
            .toArray();

        const existingFormIds = new Set(existingSections.map((section) => section.formId));
        const newSections = formSectionCacheData.filter(
            (section) => !existingFormIds.has(section.formId)
        );

        if (newSections.length > 0) {
            // Add only the new sections
            return this.formSectionsTable.bulkAdd(newSections);
        } else {
            return Promise.resolve([]);
        }
    }

    async getFormSection(section_id: number) {
        await this.ensureHealthy();
        return await this.formSectionsTable.where('formId').equals(section_id).first();
    }

    async addFormSectionElementResponse(
        section_id: number,
        formData: FormUserData
    ) {
        await this.ensureHealthy();
        const formSection = await this.getFormSection(section_id);
        if (!formSection) {
            throw new Error(`Section with ID ${section_id} not found`);
        }
        formSection.formElements = formData;
        return this.formSectionsTable.put(formSection);
    }

    async setFormSectionCompleted(section_id: number) {
        await this.ensureHealthy();
        const formSection = await this.getFormSection(section_id);
        if (!formSection) {
            throw new Error(`Section with ID ${section_id} not found`);
        }
        formSection.completed = true;
        return this.formSectionsTable.put(formSection);
    }

    async getAllFormSections() {
        await this.ensureHealthy();
        return await this.formSectionsTable.toArray();
    }

    async getCompletedSections() {
        await this.ensureHealthy();
        const sections = await this.getAllFormSections();
        return sections.filter((section) => section.completed);
    }

    async getFormSectionElementResponses(section_id: number): Promise<FormUserData> {
        await this.ensureHealthy();
        const section = await this.getFormSection(section_id);
        return section ? section.formElements : {};
    }

    async getFormElementPhoto(elementId: number): Promise<FormElementPhotos | undefined> {
        await this.ensureHealthy();
        const formElementPhotos = await this.formElementPhotosTable.where('elementId').equals(elementId).first();
        return formElementPhotos;
    }

    async addFormElementPhoto(elementId: number, photoUrl: string) {
        await this.ensureHealthy();
        const existingEntry = await this.getFormElementPhoto(elementId);
        if (existingEntry) {
            const photos = existingEntry.photos;
            photos.push(photoUrl);
            return this.formElementPhotosTable.put({ ...existingEntry, photos });
        } else {
            return this.formElementPhotosTable.add({ elementId, photos: [photoUrl] });
        }
    }

    async deleteFormElementPhoto(elementId: number, photoIdx: number) {
        await this.ensureHealthy();
        const existingEntry = await this.getFormElementPhoto(elementId);
        if (existingEntry) {
            const photos = existingEntry.photos;
            photos.splice(photoIdx, 1);
            return this.formElementPhotosTable.put({ ...existingEntry, photos });
        } else {
            throw new Error(`Photos for element ID ${elementId} not found`);
        }
    }

    async getFormElementPhotos(elementId: number): Promise<string[]> {
        await this.ensureHealthy();
        const formElementPhotos = await this.getFormElementPhoto(elementId);
        return formElementPhotos ? formElementPhotos.photos : [];
    }

    async getAllFiles() {
        await this.ensureHealthy();
        return await this.Photo.orderBy('id').toArray();
    }

    async getAllNotUploadedPhotos() {
        await this.ensureHealthy();
        return await this.NotUploadedFiles.orderBy('id').toArray();
    }

    async getFileData(photoId: number) {
        await this.ensureHealthy();
        return await this.Photo.where('photoId').equals(photoId).first();
    }

    async addFileData(
        photoId: number,
        photoName: string,
        photoTag: string,
        fileDetail: FileDetail,
        isSkipped: boolean = false
    ) {
        await this.ensureHealthy();
        const existingEntry = await this.getFileData(photoId);
        if (existingEntry) {
            return this.Photo.put({ ...existingEntry, files: [...existingEntry.files, fileDetail], isSkipped });
        } else {
            return this.Photo.add({ photoId, photoName, photoTag, hasSeenInstructions: false, files: [fileDetail], isSkipped });
        }
    }

    async addNotUploadedFileData(
        data: INotUploadedFileData
    ) {
        await this.ensureHealthy();
        return this.NotUploadedFiles.add(data);
    }


    async deleteFileData(photoId: number, fileIdx: number) {
        await this.ensureHealthy();
        const existingEntry = await this.getFileData(photoId);
        if (existingEntry) {
            const files = existingEntry.files;
            files.splice(fileIdx, 1);
            return await this.Photo.put({ ...existingEntry, files });
            // if (files.length === 0) {
            //     return await this.Photo.where('photoId').equals(photoId).delete();
            // } else {
            //     return await this.Photo.put({ ...existingEntry, files });
            // }
        } else {
            throw new Error(`File data for photo ID ${photoId} not found`);
        }
    }

    async deleteNotUploadedFileData(photoId: number) {
        await this.ensureHealthy();
        await this.NotUploadedFiles.where('photoId').equals(photoId).delete();
    }

    /**
     * Borra una fila concreta de la cola. Hace falta además del borrado por `photoId` porque al
     * reintentar conviven dos filas del mismo paso —la que se está reintentando y la que el fallo
     * acaba de re-encolar— y el borrado masivo se llevaría las dos.
     */
    async deleteNotUploadedFileById(id: number) {
        await this.ensureHealthy();
        await this.NotUploadedFiles.delete(id);
    }

    async deletePhotoIdData(photoId: number) {
        await this.ensureHealthy();
        return this.Photo.where('photoId').equals(photoId).delete();
    }


    async setHasSeenInstructions(
        photoId: number,
        photoName: string,
        photoTag: string,
        hasSeenInstructions: boolean
    ) {
        await this.ensureHealthy();
        const existingEntry = await this.getFileData(photoId);
        if (existingEntry) {
            return this.Photo.put({ ...existingEntry, hasSeenInstructions });
        } else {
            return this.Photo.add({ photoId, photoName, photoTag, hasSeenInstructions, files: [] });
        }
    }

    async updateFileData(photoId: number, data: PhotoStep) {
        await this.ensureHealthy();
        await this.Photo.put({ ...data, photoId });
    }

}